import { useEffect, useState } from 'react';
import { FunctionComponent } from 'react';
import TopMenu from '../shared/top-menu/top-menu';
import Footer from '../shared/footer/footer';
import './prices.scss';

import {
  LicenseType,
  moduleStructure,
  PSIMAGOPRO_VERSION,
  SpecialDiscountNameDE,
  SpecialDiscountPercentage,
  SPSS_VERSION,
  spssbundles,
  spssmodules,
} from './price-details';
import PricesSKU from './sku';
import OrderForm from './order-form';
import PricesTopBanner from './prices-top-banner';
import H2 from '../shared/html-tags/h2';
import H3 from '../shared/html-tags/h3';
import B from '../shared/html-tags/b';
import P from '../shared/html-tags/p';
import { Helmet } from 'react-helmet-async';
export type Props = {};

export const FormatEuro = (value: number) => {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
};

const Prices: FunctionComponent<Props> = (props) => {
  const [selectedlicenseType, setSelectedLicenseType] =
    useState<LicenseType>('authorized fixed term');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [calculatedPriceIBM, setCalculatedPriceIBM] = useState<number>(0);
  const [calculatedPricePS, setCalculatedPricePS] = useState<number>(0);
  const [calculatedPriceDiff, setCalculatedPriceDiff] = useState<number>(0);
  const [numberOfLicenses, setNumberOfLicenses] = useState<number>(1);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [specialDiscountPercentage, setSpecialDiscountPercentage] = useState<number>(17);
  const [calculatedDiscount, setCalculatedDiscount] = useState<number>(0);
  const [calculatedSpecialDiscount, setCalculatedSpecialDiscount] = useState<number>(0);
  const [selectedBundle, setSelectedBundle] = useState<
    moduleStructure & { includedModules: string[] }
  >(spssbundles[0]);

  const calculatePrice = (licenseType: LicenseType): number => {
    let pricePS = 0;

    //calculate the price for the selected modules and license type
    const bundleVariation = selectedBundle.variations.find((x) => x.lic_type === licenseType);
    if (bundleVariation) {
      pricePS += bundleVariation.PS_Imago_Price;
    }

    selectedModules.forEach((module) => {
      const selectedModule = spssmodules.find((x) => x.name === module);
      if (selectedModule && !selectedBundle.includedModules.includes(selectedModule.name)) {
        const selectedVariation = selectedModule.variations.find((x) => x.lic_type === licenseType);
        if (selectedVariation) {
          pricePS += selectedVariation.PS_Imago_Price;
        }
      }
    });

    return pricePS;
  };

  // ----------------------------------------------------------------
  useEffect(() => {
    let priceIBM = 0;
    let pricePS = 0;
    let discount = 0;

    //calculate the price for the selected modules and license type
    const bundleVariation = selectedBundle.variations.find(
      (x) => x.lic_type === selectedlicenseType,
    );
    if (bundleVariation) {
      priceIBM += bundleVariation.IBM_price;
      pricePS += bundleVariation.PS_Imago_Price;
    }

    selectedModules.forEach((module) => {
      const selectedModule = spssmodules.find((x) => x.name === module);
      if (selectedModule && !selectedBundle.includedModules.includes(selectedModule.name)) {
        const selectedVariation = selectedModule.variations.find(
          (x) => x.lic_type === selectedlicenseType,
        );
        if (selectedVariation) {
          priceIBM += selectedVariation.IBM_price;
          pricePS += selectedVariation.PS_Imago_Price;
        }
      }
    });

    //calculate the discount
    if (numberOfLicenses <= 1) {
    } else if (numberOfLicenses >= 2 && numberOfLicenses <= 4) {
      discount = 5;
    } else if (numberOfLicenses >= 5 && numberOfLicenses <= 9) {
      discount = 10;
    } else if (numberOfLicenses >= 10 && numberOfLicenses <= 19) {
      discount = 12.5;
    } else if (numberOfLicenses >= 20 && numberOfLicenses <= 49) {
      discount = 15;
    } else if (numberOfLicenses >= 50 && numberOfLicenses <= 99) {
      discount = 17.5;
    } else if (numberOfLicenses >= 100) {
      discount = 20;
    }

    setCalculatedPriceIBM(priceIBM);
    setCalculatedPricePS(pricePS);
    setCalculatedPriceDiff(priceIBM - pricePS);
    setDiscountPercentage(discount);
    setCalculatedDiscount((numberOfLicenses * pricePS * discount) / 100);

    setCalculatedSpecialDiscount(
      (numberOfLicenses * pricePS * (100 - discount) * SpecialDiscountPercentage) / 100 / 100,
    );
  }, [
    numberOfLicenses,
    selectedBundle.includedModules,
    selectedBundle.variations,
    selectedModules,
    selectedlicenseType,
    specialDiscountPercentage,
  ]);

  // ----------------------------------------------------------------
  useEffect(() => {
    //When the selected bundle changes, we need to update the selected modules
    setSelectedModules(selectedBundle.includedModules);
  }, [selectedBundle]);

  //If additional modules are selected, then check if there is a bundle that includes all of them. If so, select that bundle.
  useEffect(() => {
    const matchingBundle = spssbundles.find((bundle) =>
      selectedModules.every((module) => bundle.includedModules.includes(module)),
    );
    if (matchingBundle && matchingBundle.includedModules.length === selectedModules.length) {
      setSelectedBundle(matchingBundle);
    }
  }, [selectedModules]);

  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------

  return (
    <>
      <Helmet>
        <title>PS IMAGO PRO Preise</title>
        <meta
          name="description"
          content="Hier findest du Preise für PS IMAGO PRO, Module und Bundles, sowie die Lizenztypen."
        />
        <meta
          name="keywords"
          content="PS IMAGO PRO, Preise, Module, Bundles, Lizenztypen, Authorized User, Concurrent User"
        />
        <link rel="canonical" href="https://predictivesolutions.2x4.de/de/prices" />
      </Helmet>
      <div className="page prices">
        <TopMenu selected="prices"></TopMenu>
        <PricesTopBanner />

        <div className="page-content bg-1">
          <div className="inner-content">
            <div className="prices-table-container">
              <table className="prices-table">
                <thead>
                  <tr>
                    <td></td>
                    <td className="module-name"></td>
                    <td
                      colSpan={2}
                      style={{
                        textAlign: 'center',
                        color:
                          selectedlicenseType === 'authorized fixed term' ||
                          selectedlicenseType === 'concurrent fixed term'
                            ? 'var(--PriceDekoColor)'
                            : 'var(--RegularTextColor)',
                      }}
                      onClick={() => {
                        if (
                          !(
                            selectedlicenseType === 'authorized fixed term' ||
                            selectedlicenseType === 'concurrent fixed term'
                          )
                        ) {
                          setSelectedLicenseType('authorized fixed term');
                        }
                      }}
                    >
                      <H2 language="de">Mietlizenz</H2>
                    </td>
                    <td
                      colSpan={2}
                      style={{
                        textAlign: 'center',
                        color:
                          selectedlicenseType === 'authorized perpetual' ||
                          selectedlicenseType === 'concurrent perpetual'
                            ? 'var(--PriceDekoColor)'
                            : 'var(--RegularTextColor)',
                      }}
                      onClick={() => {
                        if (
                          !(
                            selectedlicenseType === 'authorized perpetual' ||
                            selectedlicenseType === 'concurrent perpetual'
                          )
                        ) {
                          setSelectedLicenseType('authorized perpetual');
                        }
                      }}
                    >
                      <H2 language="de">Kauflizenz</H2>
                    </td>
                  </tr>
                  <tr>
                    <td
                      className="module-name"
                      colSpan={2}
                      style={{ verticalAlign: 'bottom', paddingLeft: '2px' }}
                    ></td>
                    <td
                      style={{ textAlign: 'center' }}
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized fixed term');
                      }}
                      rowSpan={2}
                    >
                      <H3 language="de">Authorized User</H3>
                      <i
                        style={{
                          fontSize: '0.75rem',
                          lineHeight: '1',
                          display: 'block',
                        }}
                      >
                        Eine einzelne Person
                        <br />
                        darf die Software nutzen
                      </i>
                    </td>
                    <td
                      style={{ textAlign: 'center' }}
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent fixed term');
                      }}
                    >
                      <H3 language="de">Concurrent User</H3>
                      <i
                        style={{
                          fontSize: '0.75rem',
                          lineHeight: '1',
                          display: 'block',
                          marginBottom: '0.5rem',
                        }}
                      >
                        Je nach Anzahl der Lizenzen,
                        <br /> dürfen mehrere Personen <br />
                        die Software gleichzeitig nutzen
                      </i>
                    </td>
                    <td
                      style={{ textAlign: 'center' }}
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized perpetual');
                      }}
                    >
                      <H3 language="de">Authorized User</H3>
                      <i
                        style={{
                          fontSize: '0.75rem',
                          lineHeight: '1',
                          display: 'block',
                        }}
                      >
                        Eine einzelne Person
                        <br />
                        darf die Software nutzen
                      </i>
                    </td>
                    <td
                      style={{ textAlign: 'center' }}
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent perpetual');
                      }}
                    >
                      <H3 language="de">Concurrent User</H3>
                      <i
                        style={{
                          fontSize: '0.75rem',
                          lineHeight: '1',
                          display: 'block',
                          marginBottom: '0.5rem',
                        }}
                      >
                        Je nach Anzahl der Lizenzen,
                        <br /> dürfen mehrere Personen <br />
                        die Software gleichzeitig nutzen
                      </i>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {/* == Kosten pro Lizenz TOP ================================================================================= 
                  <tr>
                    <td className="module-name" colSpan={2} style={{ borderBottom: 'none' }}>
                      <H3
                        language="de"
                        style={{
                          marginBlockStart: '0',
                          color: 'var(--PriceDekoColor)',
                        }}
                      >
                        Kosten pro Lizenz:
                      </H3>
                    </td>

                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized fixed term');
                      }}
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color:
                          selectedlicenseType === 'authorized fixed term'
                            ? 'var(--PriceDekoColor)'
                            : '#dddddd',
                        fontSize: '1.25rem',
                      }}
                    >
                      <div>{FormatEuro(calculatePrice('authorized fixed term') / 12)}</div>
                      <div style={{ fontSize: '0.7rem' }}>pro Monat*</div>
                    </td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent fixed term');
                      }}
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color:
                          selectedlicenseType === 'concurrent fixed term'
                            ? 'var(--PriceDekoColor)'
                            : '#dddddd',
                        fontSize: '1.25rem',
                      }}
                    >
                      <div>{FormatEuro(calculatePrice('concurrent fixed term') / 12)}</div>
                      <div style={{ fontSize: '0.7rem' }}>pro Monat*</div>
                    </td>

                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized perpetual');
                      }}
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color:
                          selectedlicenseType === 'authorized perpetual'
                            ? 'var(--PriceDekoColor)'
                            : '#dddddd',
                        fontSize: '1.25rem',
                      }}
                    >
                      {FormatEuro(calculatePrice('authorized perpetual'))}
                      <div style={{ fontSize: '0.7rem' }}>einmaliger Kaufpreis</div>
                    </td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent perpetual');
                      }}
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color:
                          selectedlicenseType === 'concurrent perpetual'
                            ? 'var(--PriceDekoColor)'
                            : '#dddddd',
                        fontSize: '1.25rem',
                      }}
                    >
                      {FormatEuro(calculatePrice('concurrent perpetual'))}
                      <div style={{ fontSize: '0.7rem' }}>einmaliger Kaufpreis</div>
                    </td>
                  </tr>
                  */}
                  {/* == Bundle-Headline ================================================================================= */}
                  <tr>
                    <td className="module-name" colSpan={2}>
                      <H3 language="de">Diese Module sind immer dabei:</H3>
                    </td>

                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized fixed term');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent fixed term');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized perpetual');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent perpetual');
                      }}
                    ></td>
                  </tr>
                  {/* == Core Modules-Headline ================================================================================= */}
                  {spssmodules.map((module) => (
                    <>
                      {spssbundles
                        .find((x) => x.name === 'IBM SPSS Base')
                        ?.includedModules.includes(module.name) && (
                        <tr key={module.name} className="module-row">
                          <td className="module-selected">
                            <input
                              type="checkbox"
                              id={module.name}
                              onChange={(e) => {
                                if (!selectedBundle.includedModules.includes(module.name)) {
                                  if (e.target.checked && !selectedModules.includes(module.name)) {
                                    setSelectedModules([...selectedModules, module.name]);
                                  } else if (
                                    !e.target.checked &&
                                    selectedModules.includes(module.name)
                                  ) {
                                    setSelectedModules(
                                      selectedModules.filter((x) => x !== module.name),
                                    );
                                  }
                                }
                              }}
                              checked={selectedModules.includes(module.name)}
                              disabled={selectedBundle.includedModules.includes(module.name)}
                            />
                          </td>
                          <td
                            className="module-name"
                            onClick={() => {
                              if (!selectedBundle.includedModules.includes(module.name)) {
                                if (!selectedModules.includes(module.name)) {
                                  setSelectedModules([...selectedModules, module.name]);
                                } else {
                                  setSelectedModules(
                                    selectedModules.filter((x) => x !== module.name),
                                  );
                                }
                              }
                            }}
                          >
                            {module.name}
                            {module.name === 'IBM SPSS Base Statistics' && ' ' + SPSS_VERSION}
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'authorized fixed term'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('authorized fixed term');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="authorized fixed term"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'concurrent fixed term'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('concurrent fixed term');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="concurrent fixed term"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'authorized perpetual'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('authorized perpetual');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="authorized perpetual"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'concurrent perpetual'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('concurrent perpetual');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="concurrent perpetual"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  ))}

                  {/* == Bundle-Headline ================================================================================= */}
                  <tr>
                    <td className="module-name" colSpan={2}>
                      <H3 language="de">Option: Wähle ein Bundle aus:</H3>
                    </td>

                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized fixed term');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent fixed term');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized perpetual');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent perpetual');
                      }}
                    ></td>
                  </tr>
                  {/* == Bundle ================================================================================= */}
                  {spssbundles.map((bundle) => (
                    <tr key={bundle.name} className="bundle-row">
                      <td className="module-selected">
                        <input
                          type="radio"
                          id={bundle.name}
                          name="selectedBundle"
                          checked={bundle.name === selectedBundle.name}
                          onChange={() => {
                            setSelectedBundle(bundle);
                          }}
                        />
                      </td>
                      <td
                        className="bundle-name"
                        onClick={() => {
                          setSelectedBundle(bundle);
                        }}
                      >
                        {bundle.name}
                      </td>

                      <td
                        className={
                          selectedlicenseType === 'authorized fixed term'
                            ? 'selected-licence-type'
                            : ''
                        }
                        onClick={() => {
                          setSelectedLicenseType('authorized fixed term');
                        }}
                      >
                        <PricesSKU
                          module={bundle}
                          lic_type="authorized fixed term"
                          selectedBundle={selectedBundle}
                          selectedLicType={selectedlicenseType}
                          moduleSelected={selectedBundle === bundle}
                          showPrice={false}
                        />
                      </td>

                      <td
                        className={
                          selectedlicenseType === 'concurrent fixed term'
                            ? 'selected-licence-type'
                            : ''
                        }
                        onClick={() => {
                          setSelectedLicenseType('concurrent fixed term');
                        }}
                      >
                        <PricesSKU
                          module={bundle}
                          lic_type="concurrent fixed term"
                          selectedBundle={selectedBundle}
                          selectedLicType={selectedlicenseType}
                          moduleSelected={selectedBundle === bundle}
                          showPrice={false}
                        />
                      </td>

                      <td
                        className={
                          selectedlicenseType === 'authorized perpetual'
                            ? 'selected-licence-type'
                            : ''
                        }
                        onClick={() => {
                          setSelectedLicenseType('authorized perpetual');
                        }}
                      >
                        <PricesSKU
                          module={bundle}
                          lic_type="authorized perpetual"
                          selectedBundle={selectedBundle}
                          selectedLicType={selectedlicenseType}
                          moduleSelected={selectedBundle === bundle}
                          showPrice={false}
                        />
                      </td>

                      <td
                        className={
                          selectedlicenseType === 'concurrent perpetual'
                            ? 'selected-licence-type'
                            : ''
                        }
                        onClick={() => {
                          setSelectedLicenseType('concurrent perpetual');
                        }}
                      >
                        <PricesSKU
                          module={bundle}
                          lic_type="concurrent perpetual"
                          selectedBundle={selectedBundle}
                          selectedLicType={selectedlicenseType}
                          moduleSelected={selectedBundle === bundle}
                          showPrice={false}
                        />
                      </td>
                    </tr>
                  ))}
                  {/* == Modules-Headline ================================================================================= */}
                  <tr>
                    <td className="module-name" colSpan={2}>
                      <H3 language="de">Wähle weitere Module aus:</H3>
                    </td>

                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized fixed term');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent fixed term');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('authorized perpetual');
                      }}
                    ></td>

                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      onClick={() => {
                        setSelectedLicenseType('concurrent perpetual');
                      }}
                    ></td>
                  </tr>
                  {/* == Modules ================================================================================= */}
                  {spssmodules.map((module) => (
                    <>
                      {!spssbundles
                        .find((x) => x.name === 'IBM SPSS Base')
                        ?.includedModules.includes(module.name) && (
                        <tr key={module.name} className="module-row">
                          <td className="module-selected">
                            <input
                              type="checkbox"
                              id={module.name}
                              onChange={(e) => {
                                if (!selectedBundle.includedModules.includes(module.name)) {
                                  if (e.target.checked && !selectedModules.includes(module.name)) {
                                    setSelectedModules([...selectedModules, module.name]);
                                  } else if (
                                    !e.target.checked &&
                                    selectedModules.includes(module.name)
                                  ) {
                                    setSelectedModules(
                                      selectedModules.filter((x) => x !== module.name),
                                    );
                                  }
                                }
                              }}
                              checked={selectedModules.includes(module.name)}
                              disabled={selectedBundle.includedModules.includes(module.name)}
                            />
                          </td>
                          <td
                            className="module-name"
                            onClick={() => {
                              if (!selectedBundle.includedModules.includes(module.name)) {
                                if (!selectedModules.includes(module.name)) {
                                  setSelectedModules([...selectedModules, module.name]);
                                } else {
                                  setSelectedModules(
                                    selectedModules.filter((x) => x !== module.name),
                                  );
                                }
                              }
                            }}
                          >
                            {module.name}
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'authorized fixed term'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('authorized fixed term');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="authorized fixed term"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'concurrent fixed term'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('concurrent fixed term');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="concurrent fixed term"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'authorized perpetual'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('authorized perpetual');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="authorized perpetual"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>

                          <td
                            className={
                              selectedlicenseType === 'concurrent perpetual'
                                ? 'selected-licence-type'
                                : ''
                            }
                            onClick={() => {
                              setSelectedLicenseType('concurrent perpetual');
                            }}
                          >
                            <PricesSKU
                              module={module}
                              lic_type="concurrent perpetual"
                              selectedBundle={selectedBundle}
                              selectedLicType={selectedlicenseType}
                              moduleSelected={selectedModules.includes(module.name)}
                              showPrice={false}
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  ))}

                  {/* == Kosten pro Lizenz ================================================================================= 
                  <tr>
                    <td style={{ borderBottom: 'none' }}></td>
                    <td style={{ borderBottom: 'none', fontWeight: 'bold' }}>Kosten pro Lizenz</td>
                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized fixed term' && (
                        <>{FormatEuro(calculatedPricePS)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent fixed term' && (
                        <>{FormatEuro(calculatedPricePS)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized perpetual' && (
                        <>{FormatEuro(calculatedPricePS)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent perpetual' && (
                        <>{FormatEuro(calculatedPricePS)}</>
                      )}
                    </td>
                  </tr>
*/}
                  {/* == Anzahl Lizenzen =================================================================================
                  <tr>
                    <td style={{ borderBottom: 'none' }}></td>
                    <td style={{ borderBottom: 'none', fontWeight: 'bold' }}>Anzahl Lizenzen:</td>
                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized fixed term' && (
                        <input
                          type="number"
                          value={numberOfLicenses}
                          min={1}
                          onChange={(e) => {
                            setNumberOfLicenses(Number(e.target.value));
                          }}
                        />
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent fixed term' && (
                        <input
                          type="number"
                          value={numberOfLicenses}
                          min={1}
                          onChange={(e) => {
                            setNumberOfLicenses(Number(e.target.value));
                          }}
                        />
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized perpetual' && (
                        <input
                          type="number"
                          value={numberOfLicenses}
                          min={1}
                          onChange={(e) => {
                            setNumberOfLicenses(Number(e.target.value));
                          }}
                        />
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent perpetual' && (
                        <input
                          type="number"
                          value={numberOfLicenses}
                          min={1}
                          onChange={(e) => {
                            setNumberOfLicenses(Number(e.target.value));
                          }}
                        />
                      )}
                    </td>
                  </tr>
 */}
                  {/* == Summe ================================================================================= 
                  <tr>
                    <td style={{ borderBottom: 'none' }}></td>
                    <td style={{ borderBottom: 'none', fontWeight: 'bold' }}>Summe:</td>
                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized fixed term' && (
                        <>{FormatEuro(numberOfLicenses * calculatedPricePS)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent fixed term' && (
                        <>{FormatEuro(numberOfLicenses * calculatedPricePS)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized perpetual' && (
                        <>{FormatEuro(numberOfLicenses * calculatedPricePS)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent perpetual' && (
                        <>{FormatEuro(numberOfLicenses * calculatedPricePS)}</>
                      )}
                    </td>
                  </tr>
*/}
                  {/* == Mengenrabatt ================================================================================= 
                  <tr>
                    <td style={{ borderBottom: 'none' }}></td>
                    <td style={{ borderBottom: 'none', fontWeight: 'bold' }}>
                      Mengenabatt: {discountPercentage}%
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized fixed term' && (
                        <>- {FormatEuro(calculatedDiscount)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent fixed term' && (
                        <>- {FormatEuro(calculatedDiscount)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'authorized perpetual' && (
                        <>- {FormatEuro(calculatedDiscount)}</>
                      )}
                    </td>
                    <td
                      className={
                        selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : ''
                      }
                      style={{
                        borderBottom: 'none',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedlicenseType === 'concurrent perpetual' && (
                        <>- {FormatEuro(calculatedDiscount)}</>
                      )}
                    </td>
                  </tr>
                  */}

                  {/* == Special Discount  ================================================================================= 
                  {SpecialDiscountPercentage > 0 && (
                    <tr>
                      <td style={{ borderBottom: 'none' }}></td>
                      <td style={{ borderBottom: 'none' }}>
                        <B language="de">
                          {SpecialDiscountNameDE}: {SpecialDiscountPercentage}%
                        </B>
                      </td>
                      <td
                        className={
                          selectedlicenseType === 'authorized fixed term'
                            ? 'selected-licence-type'
                            : ''
                        }
                        style={{
                          borderBottom: 'none',
                          textAlign: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        {selectedlicenseType === 'authorized fixed term' && (
                          <>- {FormatEuro(calculatedSpecialDiscount)}</>
                        )}
                      </td>
                      <td
                        className={
                          selectedlicenseType === 'concurrent fixed term'
                            ? 'selected-licence-type'
                            : ''
                        }
                        style={{
                          borderBottom: 'none',
                          textAlign: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        {selectedlicenseType === 'concurrent fixed term' && (
                          <>- {FormatEuro(calculatedSpecialDiscount)}</>
                        )}
                      </td>
                      <td
                        className={
                          selectedlicenseType === 'authorized perpetual'
                            ? 'selected-licence-type'
                            : ''
                        }
                        style={{
                          borderBottom: 'none',
                          textAlign: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        {selectedlicenseType === 'authorized perpetual' && (
                          <>- {FormatEuro(calculatedSpecialDiscount)}</>
                        )}
                      </td>
                      <td
                        className={
                          selectedlicenseType === 'concurrent perpetual'
                            ? 'selected-licence-type'
                            : ''
                        }
                        style={{
                          borderBottom: 'none',
                          textAlign: 'center',
                          fontWeight: 'bold',
                        }}
                      >
                        {selectedlicenseType === 'concurrent perpetual' && (
                          <>- {FormatEuro(calculatedSpecialDiscount)}</>
                        )}
                      </td>
                    </tr>
                  )}
*/}
                  {/* == Gesamtkosten ================================================================================= 
                  <tr>
                    <td className="module-selected" style={{ border: 'none' }}></td>
                    <td className="total-price">
                      Gesamtkosten:
                      <br />
                      <i style={{ fontSize: '0.8rem' }}>zzgl. gesetzlicher USt.</i>
                    </td>

                    <td
                      className={
                        'total-price ' +
                        (selectedlicenseType === 'authorized fixed term'
                          ? 'selected-licence-type'
                          : '')
                      }
                      style={{ textAlign: 'center' }}
                    >
                      {selectedlicenseType === 'authorized fixed term' && (
                        <>
                          {FormatEuro(
                            numberOfLicenses * calculatedPricePS -
                              calculatedDiscount -
                              calculatedSpecialDiscount,
                          )}
                          {//<br /><BuyNowButton order={'buy'} />
                          }
                        </>
                      )}
                    </td>

                    <td
                      className={
                        'total-price ' +
                        (selectedlicenseType === 'concurrent fixed term'
                          ? 'selected-licence-type'
                          : '')
                      }
                      style={{ textAlign: 'center' }}
                    >
                      {selectedlicenseType === 'concurrent fixed term' && (
                        <>
                          {FormatEuro(
                            numberOfLicenses * calculatedPricePS -
                              calculatedDiscount -
                              calculatedSpecialDiscount,
                          )}
                          {//<br /><BuyNowButton order={'buy'} />
                          }
                        </>
                      )}
                    </td>

                    <td
                      className={
                        'total-price ' +
                        (selectedlicenseType === 'authorized perpetual'
                          ? 'selected-licence-type'
                          : '')
                      }
                      style={{ textAlign: 'center' }}
                    >
                      {' '}
                      {selectedlicenseType === 'authorized perpetual' && (
                        <>
                          {FormatEuro(
                            numberOfLicenses * calculatedPricePS -
                              calculatedDiscount -
                              calculatedSpecialDiscount,
                          )}
                          {//<br /><BuyNowButton order={'buy'} />
                          }
                        </>
                      )}
                    </td>

                    <td
                      className={
                        'total-price ' +
                        (selectedlicenseType === 'concurrent perpetual'
                          ? 'selected-licence-type'
                          : '')
                      }
                      style={{ textAlign: 'center' }}
                    >
                      {selectedlicenseType === 'concurrent perpetual' && (
                        <>
                          {FormatEuro(
                            numberOfLicenses * calculatedPricePS -
                              calculatedDiscount -
                              calculatedSpecialDiscount,
                          )}
                          {//<br /><BuyNowButton order={'buy'} />
                          }
                        </>
                      )}
                    </td>
                  </tr>
                  */}
                </tbody>
              </table>
            </div>
            {/* 
            <div style={{ fontSize: '0.8rem', marginTop: '1rem' }}>
              <b style={{ color: 'crimson' }}>*</b> Die monatlichen Kosten dienen dir zur
              Orientierung. Die Rechnungstellung erfolgt immer für 12 Monate im Vorraus.
            </div>
            */}
            {/* == Unser Angebot ================================================================================= */}
            <div className="cost-summary">
              <H2 language="de">Unser Angebot für dich:</H2>
              <b>PS IMAGO PRO {PSIMAGOPRO_VERSION}</b> {' - '}
              {selectedlicenseType === 'authorized fixed term' && (
                <>
                  <b style={{ color: 'var(--PriceDekoColor)' }}>
                    Mietlizenz für {numberOfLicenses} Authorized User
                  </b>{' '}
                  für 12-Monate
                </>
              )}
              {selectedlicenseType === 'concurrent fixed term' && (
                <>
                  <b style={{ color: 'var(--PriceDekoColor)' }}>
                    Mietlizenz für {numberOfLicenses} Concurrent User für 12 Monate
                  </b>{' '}
                </>
              )}
              {selectedlicenseType === 'authorized perpetual' && (
                <>
                  <b style={{ color: 'var(--PriceDekoColor)' }}>
                    Kauflizenz für {numberOfLicenses} Authorized User
                  </b>
                  , inkl. 12 Monate Support & Maintenance
                </>
              )}
              {selectedlicenseType === 'concurrent perpetual' && (
                <>
                  <b style={{ color: 'var(--PriceDekoColor)' }}>
                    Kauflizenz für {numberOfLicenses} Concurrent User
                  </b>
                  , inkl. 12 Monate Support & Maintenance
                </>
              )}{' '}
              - inkl. der folgenden Module:
              <ul>
                {selectedModules.map((module) => (
                  <li key={module}>
                    <b>
                      {module} {module === 'IBM SPSS Base Statistics' && SPSS_VERSION}
                    </b>{' '}
                    -{' '}
                    <span style={{ fontSize: '0.7rem' }}>
                      {
                        spssmodules
                          .find((x) => x.name === module)
                          ?.variations.find((x) => x.lic_type === selectedlicenseType)?.sku
                      }
                    </span>
                  </li>
                ))}
              </ul>
              {/* 
              <P language="de">
                Gesamtkosten:{' '}
                {(selectedlicenseType === 'authorized perpetual' ||
                  selectedlicenseType === 'concurrent perpetual') && <> Einmalig </>}
                <b style={{ color: 'var(--PriceDekoColor)' }}>
                  {FormatEuro(
                    numberOfLicenses * calculatedPricePS -
                      calculatedDiscount -
                      calculatedSpecialDiscount,
                  )}
                </b>{' '}
                {(selectedlicenseType === 'authorized fixed term' ||
                  selectedlicenseType === 'concurrent fixed term') && <> pro Jahr </>}
                zzgl. ges. USt.
              </P>
              */}
              {/* 
            <P language="de">
              Gegenüber dem regulären IBM SPSS Statistics Preis von{' '}
              {FormatEuro(numberOfLicenses * calculatedPriceIBM)} sparst Du{' '}
              {FormatEuro(
                numberOfLicenses * calculatedPriceIBM -
                  (numberOfLicenses * calculatedPricePS -
                    calculatedDiscount -
                    calculatedSpecialDiscount)
              )}
              , obwohl Du mit PS IMAGO PRO einen deutlich größeren Funktionsumfang bekommst als mit
              IBM SPSS Statistics alleine.
            </P>
            <br />
              */}
              <div className="form-block">
                <OrderForm
                  source={'predictivesolutions.2x4.de/prices'}
                  numberOfLicenses={numberOfLicenses}
                  selectedLicenseType={selectedlicenseType}
                  selectedBundle={selectedBundle.name}
                  selectedModules={selectedModules}
                  calculatedPricePS={calculatedPricePS}
                  calculatedPriceTotal={
                    numberOfLicenses * calculatedPricePS -
                    calculatedDiscount -
                    calculatedSpecialDiscount
                  }
                  calculatedDiscount={calculatedDiscount}
                  calculatedSpecialDiscount={calculatedSpecialDiscount}
                  specialDiscountName={SpecialDiscountNameDE}
                />
                <div style={{ marginLeft: '3rem', fontSize: '0.9rem' }}>
                  <h3>Wichtige Hinweise</h3>
                  <p>
                    <strong>PS IMAGO PRO</strong> ist ausschließlich für Windows-Computer verfügbar.
                    Wenn du einen Mac nutzt, prüfe bitte, ob du mit einem Windows-Emulator wie{' '}
                    <em>Parallels Desktop</em> arbeiten kannst.
                  </p>

                  <p>
                    <strong>Verfügbarkeit:</strong> Du kannst <strong>PS IMAGO PRO</strong> weltweit
                    in den meisten Ländern kaufen. Unser spezielles <strong>2x4-Angebot</strong>{' '}
                    gilt jedoch nur für: Deutschland, Österreich, Schweiz, Liechtenstein, Norwegen,
                    Schweden, Dänemark und Island. In anderen Ländern können die Preise variieren.
                    Gerne helfen wir dir, den passenden <em>Predictive Solutions</em> Partner zu
                    finden.
                  </p>

                  <h3>So funktioniert der Bestellprozess</h3>
                  {/* 
                  <p>
                    Du hast zwei Möglichkeiten: <strong>ein Angebot anfordern</strong> oder{' '}
                    <strong>direkt bestellen</strong>. Kläre bitte mit deiner Einkaufsabteilung, ob
                    für die Rechnungstellung eine Bestellnummer oder Referenz benötigt wird.
                  </p>
*/}
                  <p>
                    Unser Angebot erhältst du klassisch per E-Mail. Falls es für dich passt, kannst
                    du die Bestellung einfach per E-Mail bestätigen.
                  </p>
                  {/* 
                  <p>
                    Wenn du dich für eine sofortige Bestellung entscheidest, kontaktieren wir dich
                    anschließend, um sicherzustellen, dass alles korrekt ist. Danach erhältst du die
                    Rechnung sowie den Download-Link für <strong>PS IMAGO PRO</strong> per E-Mail.
                  </p>
                  */}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* =================================================================================== */}
        <div className="page-content bg-2">
          <div className="inner-content" style={{ paddingTop: '0' }}>
            <H2 language="de">Wo ist der Haken?</H2>
            <ul>
              <li>
                <P language="de">Es gibt keinen.</P>
              </li>
              <li>
                <P language="de">
                  PS IMAGO PRO findest Du im offiziellen IBM Produktkatalog mit dem folgenden Link:{' '}
                  <a
                    href="https://www.ibm.com/partnerplus/directory/solution/0054"
                    target="_blank"
                    rel="noreferrer"
                  >
                    IBM Partner Portal
                  </a>
                </P>
              </li>
            </ul>

            <H2 language="de">Gut zu wissen:</H2>
            <ul>
              <li>
                <P language="de">
                  PS IMAGO PRO enthält immer auch die SPSS Statistics Lizenzen, welche Du bei der
                  Bestellung ausgewählt hast. Module kannst Du auch später hinzufügen, solange Du
                  die Lizenz mietest, oder Du die jährliche Support & Maintenance Vereinbarung
                  gebucht hast. Eine bestehende SPSS Statistics Lizenz kannst Du selbstverständlich
                  weiter benutzen oder gegebenenfalls weiter verkaufen.
                </P>
              </li>
              <li>
                <P language="de">
                  Die Software ist nur für Windows verfügbar. Eine Mac oder Linux Version gibt es
                  nicht.
                </P>
              </li>

              <li>
                <P language="de">
                  PS IMAGO PRO kannst Du weltweit in den meisten Ländern kaufen. Unser Angebot von
                  2x4 besteht für folgende Länder: Deutschland, Österreich, Schweiz, Liechtenstein,
                  Norwegen, Schweden, Dänemark, Island.
                  <br /> In anderen Ländern kann es sein, dass die Preise abweichen. Gerne
                  unterstützen wir Dich dabei, dort den richtigen Predictive Solutions Partner zu
                  finden.
                </P>
              </li>

              <li>
                <P language="de">
                  Lizenzen für Authorized User sind an eine bestimmte Person gebunden und können auf
                  einem PC installiert werden. Das ist übrigens bei IBM genau so, auch wenn es
                  früher einmal eine 2. Installationsmöglichkeit auf einem Laptop gegeben hat. Bei
                  einem Wechsel des Computers oder Mitarbeiters senden wir Dir aber natürlich
                  kostenlos einen neuen Lizenzcode für die Installation zu.
                  <br />
                  Lizenzen für Concurrent User sind an eine bestimmte Anzahl von gleichzeitigen
                  Benutzungen gebunden. Das bedeutet, dass Du so viele Lizenzen benötigst, wie Du
                  gleichzeitig Benutzer haben möchtest. Die Software kann auf beliebig vielen
                  Computern installiert werden, aber nur so viele Benutzer können gleichzeitig
                  arbeiten, wie Lizenzen gekauft oder gemietet wurden. Für die Verwendung von
                  Concurrent Usern wird ein Lizenzserver installiert, der sich um die Verwaltung der
                  gleichzeitigen Zugriffe kümmert. Kontaktiere uns einfach, wenn Du zur Lizenzierung
                  Fragen hast.
                </P>
              </li>

              <li>
                <P language="de">
                  Beim Kauf von PS IMAGO PRO sind bereits 12 Monate Support & Maintenance inklusive.
                  In dieser Zeit erhälst Du von uns automatische Updates und wir stehen Dir für
                  technischen Support zur Verfügung. Nach Ablauf der 12 Monate kannst Du
                  entscheiden, ob Du weitere 12 Monate für 25% des Kaufpreises buchen möchtest (+ 6%
                  Steigerung pro Jahr). Wenn Du PS IMAGO PRO mietest, ist Support & Maintenance
                  bereits in der Jahresmiete enthalten und es kommen keine weiteren Kosten auf Dich
                  zu.
                </P>
              </li>

              <li>
                <P language="de">
                  Du erhältst Updates und Support zu PS IMAGO PRO und SPSS Statistics direkt von
                  2x4, solange Du die Software mietest, oder es eine aktive Support & Maintenance
                  Vereinbarung mit 2x4 gibt. Sende uns einfach eine E-Mail an{' '}
                  <a href="mailto:support@2x4.de?subject=Ich brauche Hilfe bei PS IMAGO PRO">
                    support@2x4.de
                  </a>{' '}
                  oder benutze unser <a href="/contact">Kontaktformular</a>
                </P>
              </li>
            </ul>
          </div>
        </div>

        <Footer></Footer>
      </div>
    </>
  );
};

export default Prices;
